import { useState } from 'react';
import { Drawer, Form, Input, Button, App } from 'antd';
import { usePortalCtx } from '../context.js';
import { apiPost } from '../api.js';

type NewSitePayload = {
  key: string;
  name: string;
  domain?: string;
  defaultLanguage?: 'en' | 'ar';
};

export default function CreateSiteDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<NewSitePayload>();
  const { registryUrl, setSiteKey } = usePortalCtx();
  const { message } = App.useApp();

  async function onSubmit(values: NewSitePayload) {
    setSubmitting(true);
    try {
      const url = new URL('/admin/sites', registryUrl);
      const created = await apiPost<{ key: string }>(url.toString(), values);
      message.success('Site created');
      setSiteKey(created.key);
      onClose();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Failed to create site');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      title="Create Site"
      width={520}
      open={open}
      onClose={onClose}
      destroyOnClose
      maskClosable={!submitting}
    >
      <Form form={form} layout="vertical" onFinish={onSubmit} disabled={submitting}>
        <Form.Item
          label="Key"
          name="key"
          rules={[
            { required: true, message: 'Enter a unique key' },
            { pattern: /^[a-z0-9-]{3,40}$/, message: 'Use lowercase, digits, dashes (3–40)' },
          ]}
        >
          <Input placeholder="e.g. howth-yc" autoFocus />
        </Form.Item>

        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Enter a site name' }, { min: 3 }]}
        >
          <Input placeholder="e.g. Howth Yacht Club" />
        </Form.Item>

        <Form.Item
          label="Primary domain"
          name="domain"
          rules={[
            {
              pattern: /^([a-z0-9-]+\.)+[a-z]{2,}$/i,
              message: 'Enter a valid domain (optional)',
            },
          ]}
        >
          <Input placeholder="e.g. example.com (optional)" />
        </Form.Item>

        <Form.Item label="Default language" name="defaultLanguage" initialValue="en">
          <Input placeholder="en or ar" />
        </Form.Item>

        <Form.Item style={{ marginTop: 16 }}>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Create
          </Button>
          <Button style={{ marginLeft: 8 }} onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
